import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, AppBar, Box, Button, Card, CardContent, Container, Stack, Tab, Tabs, Typography } from '@mui/material'
import { Navigate, useNavigate } from 'react-router-dom'
import adminStore from './adminStore'
import { matchesImpersonationTarget } from './impersonationTarget'
import { membershipRoleLabel } from '../tenancy/organizationPresentation'

const invalidationCodes = new Set(['ADMIN_SESSION_EXPIRED', 'ADMIN_FORBIDDEN', 'ADMIN_NOT_FOUND'])
const errorCode = error => error?.response?.data?.error?.code || error?.code || error?.message || 'ADMIN_ERROR'

const ImpersonationView = observer(() => {
	const navigate = useNavigate()
	const [context, setContext] = useState(null)
	const [items, setItems] = useState([])
	const [tab, setTab] = useState('objects')
	const [error, setError] = useState(null)
	const requestEpoch = useRef(0)
	const impersonating = adminStore.impersonating
	const impersonationEpoch = adminStore.impersonationEpoch

	const load = async current => {
		const startedAt = adminStore.impersonationEpoch
		const tuple = adminStore.impersonation
		const currentRequest = ++requestEpoch.current
		try {
			const ctx = await adminStore.client.impersonationGet('/api/impersonation/context')
			const list = await adminStore.client.impersonationGet(`/api/impersonation/${current}`, { page: 1, pageSize: 50 })
			if (requestEpoch.current !== currentRequest || adminStore.impersonationEpoch !== startedAt || !matchesImpersonationTarget(ctx, tuple)) return
			setContext(ctx); setItems(list.items); setError(null)
		} catch (requestError) {
			if (requestEpoch.current !== currentRequest || adminStore.impersonationEpoch !== startedAt) return
			const code = errorCode(requestError)
			if (invalidationCodes.has(code)) adminStore.invalidateImpersonation(code)
			else setError(code)
		}
	}

	useEffect(() => {
		if (impersonating) load(tab)
		return () => { requestEpoch.current += 1 }
	}, [tab, impersonating, impersonationEpoch])

	if (!impersonating) {
		if (adminStore.impersonationInvalidation) return <Container sx={{ py: 8 }}><Alert severity="warning">Диагностическая сессия отозвана или истекла. Данные её точного контекста очищены; другая организация или профиль участника не выбраны.</Alert><Button sx={{ mt: 2 }} onClick={() => navigate('/admin/users', { replace: true })}>Вернуться к администрированию платформы</Button></Container>
		return <Navigate to="/admin/users" replace />
	}

	const end = async () => { await adminStore.endImpersonation(); navigate('/admin/users', { replace: true }) }
	const tuple = context || adminStore.impersonation
	const effectiveEmail = tuple?.effectiveUser?.email
	const organizationName = tuple?.organization?.name || `Организация #${tuple?.organization?.id}`
	const membershipRole = membershipRoleLabel(tuple?.membership?.role)
	return <Box minHeight="100vh" bgcolor="background.default"><AppBar position="sticky" color="warning"><Stack direction="row" justifyContent="space-between" alignItems="center" px={3} py={1}><Box><Typography variant="h6">Администрирование платформы: {effectiveEmail} · Только чтение</Typography><Typography variant="body2">Организация: {organizationName} · Роль: {membershipRole} · Администратор: {tuple?.actor?.email || '—'}</Typography></Box><Button variant="contained" color="error" onClick={end}>Завершить просмотр</Button></Stack></AppBar><Container maxWidth="lg" sx={{ py: 3 }}><Alert severity="warning" sx={{ mb: 2 }}>Это отдельная диагностическая сессия только для просмотра. Даже если пользователь является владельцем или администратором, изменение объектов, заявок, клиентов, медиа, назначений, классифайдов, оплаты, настроек организации, приглашений и ИИ недоступно.</Alert>{error && <Alert severity="error">{error}</Alert>}<Tabs value={tab} onChange={(_, value) => setTab(value)}><Tab value="objects" label="Объекты"/><Tab value="requests" label="Заявки"/></Tabs><Stack spacing={1} mt={2}>{items.map(item => <Card key={item.id}><CardContent><Typography>#{item.id} · {item.renderTitle || item.title || 'Без названия'}</Typography><Typography variant="body2">{item.city || item.status || ''} · {item.createdAt || item.created_at}</Typography></CardContent></Card>)}</Stack></Container></Box>
})

export default ImpersonationView
